/**
 * @description Button to print the Claim Status Response.
 */
import { useRef, useState, useMemo } from 'react';
import _ from 'lodash';
import { Button as AntButton, ButtonProps, Row, Col, Divider } from 'antd';
import { useReactToPrint } from 'react-to-print';

/**
 * interface for props
 */
export interface IProps {
	patientName?: string;
	exportItems?: ClaimStatusResponse.IExportSchema[];
}

const claimResponsePrintExcludedKeys = new Set<string>(['submitted-claim', 'activity-log', 'uploaded-files', 'upload-files']);

const claimResponsePrintPageStyle = `
	@page {
		margin: 30px;
	}
`;

const exportNodeStyles: React.CSSProperties = {
	position: 'fixed',
	left: 0,
	top: 0,
	width: 0,
	height: 0,
	visibility: 'hidden',
};

const isPrintableClaimResponseItem = (item?: ClaimStatusResponse.IExportSchema | null): item is ClaimStatusResponse.IExportSchema =>
	!!item && !claimResponsePrintExcludedKeys.has(item.key) && !_.isEmpty(item?.pdfExport?.content);

const getPrintableClaimResponseItems = (exportItems?: ClaimStatusResponse.IExportSchema[]): ClaimStatusResponse.IExportSchema[] =>
	_.orderBy((exportItems || []).filter(isPrintableClaimResponseItem), 'pdfExport.order');

const hasPrintableClaimResponseItems = (exportItems?: ClaimStatusResponse.IExportSchema[]): boolean =>
	getPrintableClaimResponseItems(exportItems).length > 0;

/**
 * PrintClaimResponse
 *
 */
const PrintClaimResponse: React.FC<IProps & ButtonProps> = ({ patientName, exportItems, ...restProps }) => {
	const [isPrinting, setIsPrinting] = useState(false);
	const exportRef = useRef(null) as React.RefObject<HTMLDivElement>;
	const printableItems = useMemo(() => getPrintableClaimResponseItems(exportItems), [exportItems]);

	const printResponse = useReactToPrint({
		documentTitle: `Claim-Response-${_.kebabCase(patientName)}`,
		contentRef: exportRef,
		bodyClass: 'eligibility-export-node-content claim-response-export-node-content',
		pageStyle: claimResponsePrintPageStyle,
	});

	const handlePrint = () => {
		if (!isPrinting) {
			setIsPrinting(true);
		}
		setTimeout(printResponse, 100);
	};

	const exportContent = useMemo(() => {
		return (
			<Row ref={exportRef} gutter={[0, 16]} className='w-full text-[#262626]'>
				{_.map(printableItems, (item) => (
					<Col span={24} key={item.key} className='mb-3 break-inside-avoid'>
						<h2 className='m-0 text-[14px] leading-[1.2] font-semibold text-[#1f1f1f]'>{item.label}</h2>
						<Divider className='mt-1.5 mb-2' />
						{item.pdfExport.content}
					</Col>
				))}
			</Row>
		);
	}, [printableItems]);

	if (_.isEmpty(printableItems)) {
		return null;
	}

	return (
		<>
			<AntButton type='text' color='default' onClick={handlePrint} {...restProps}>
				{'Print/Download Response'}
			</AntButton>
			{isPrinting && <div style={exportNodeStyles}>{exportContent}</div>}
		</>
	);
};

export { exportNodeStyles, getPrintableClaimResponseItems, hasPrintableClaimResponseItems };
export default PrintClaimResponse;
